import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';

export interface LcPositionWithPool {
  positionId: string;
  poolId: string;
  lpShares: string;
  createdAt: string;
  updatedAt: string;
  pool?: {
    poolId: string;
    name: string;
    tokenASymbol: string;
    tokenBSymbol: string;
    reserveA: string;
    reserveB: string;
    totalLpShares: string;
    feeBps: number;
    status: string;
  };
}

interface UseMyPositionsReturn {
  positions: LcPositionWithPool[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useMyLiquidityCachePositions(): UseMyPositionsReturn {
  const [positions, setPositions] = useState<LcPositionWithPool[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchPositions = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getMyLiquidityCachePositions();
      if (fetchIdRef.current !== id) return;
      setPositions(result.positions);
      setTotal(result.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load positions');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchPositions();
  }, [fetchPositions]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { positions, total, loading, error, refresh };
}
